import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { shadow } from '../theme/tokens';
import { GEIST_MONO } from '../theme/typography';
import { CompanyAvatar } from '../components/shared/CompanyAvatar';
import { PersonAvatar } from '../components/shared/PersonAvatar';
import {
  useGlobalSearch,
  SEARCH_GROUP_CAP,
  type SearchOption,
} from './hooks/useGlobalSearch';

/**
 * Top-bar global search. An accessible combobox (MUI `Autocomplete`) over the
 * Projects + People lists supplied by `useGlobalSearch`. Selecting a project
 * opens its detail page; selecting a person opens the Directory focused on that
 * card. ⌘K / Ctrl-K focuses the field. Hidden below `md` (mobile uses the
 * drawer) — matching the previous placeholder's behaviour.
 */
export function GlobalSearch() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const boxRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Resolve the input via the wrapper rather than a TextField `inputRef`, which
  // would clobber the ref MUI Autocomplete needs internally.
  const focusInput = () => boxRef.current?.querySelector('input')?.focus();

  const { query, setQuery, activate, loading, options, projectsTotal, peopleTotal } =
    useGlobalSearch();

  // ⌘K / Ctrl-K focuses the search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        focusInput();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSelect = (option: SearchOption) => {
    if (option.type === 'project') {
      navigate(`/projects/${option.id}`);
    } else {
      // Keep the Directory's own query string when the search was run *from*
      // the Directory, so jumping to a person does not silently drop the view
      // they are in — `?view=chart` lives there, and a bare `/directory` throws
      // it away, sending a chart user back to the cards. Only from the
      // Directory: carrying another page's parameters across would be worse
      // than losing them.
      const search = location.pathname === '/directory' ? location.search : '';
      navigate({ pathname: '/directory', search }, { state: { focusEmployeeId: option.id } });
    }
    setQuery('');
    setOpen(false);
    (boxRef.current?.querySelector('input') as HTMLInputElement | null)?.blur();
  };

  const insetShadow =
    theme.palette.mode === 'dark' ? shadow.dark.inset : shadow.light.inset;

  return (
    <Box ref={boxRef} sx={{ display: { xs: 'none', md: 'block' }, width: 360, maxWidth: '40vw', flexShrink: 0 }}>
      <Autocomplete<SearchOption, false, false, true>
        freeSolo
        fullWidth
        size="small"
        open={open && query.trim().length > 0}
        onOpen={() => setOpen(true)}
        onClose={(_e, reason) => {
          setOpen(false);
          // Esc closes the results, clears the query, and blurs (SPEC §6.1).
          if (reason === 'escape') {
            setQuery('');
            (boxRef.current?.querySelector('input') as HTMLInputElement | null)?.blur();
          }
        }}
        options={options}
        filterOptions={(opts) => opts}
        groupBy={(o) => o.group}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.primary)}
        value={null}
        blurOnSelect
        clearOnBlur
        loading={loading}
        loadingText="Searching…"
        noOptionsText="No matching projects or people"
        inputValue={query}
        onInputChange={(_e, value, reason) => {
          if (reason === 'input') {
            setQuery(value);
            setOpen(value.trim().length > 0);
          }
        }}
        onChange={(_e, value, reason) => {
          if (reason === 'selectOption' && value && typeof value !== 'string') {
            handleSelect(value);
          }
        }}
        onFocus={activate}
        renderGroup={(params) => {
          const total = params.group === 'Projects' ? projectsTotal : peopleTotal;
          const hidden = total - SEARCH_GROUP_CAP;
          return (
            <li key={params.key}>
              <Typography
                component="div"
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 0.5,
                  fontSize: '0.71875rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                }}
              >
                {params.group}
              </Typography>
              <Box component="ul" sx={{ p: 0, m: 0 }}>
                {params.children}
              </Box>
              {hidden > 0 && (
                <Typography
                  component="div"
                  sx={{ px: 1.5, py: 0.5, fontSize: '0.71875rem', color: 'text.disabled' }}
                >
                  +{hidden} more
                </Typography>
              )}
            </li>
          );
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as { key?: string } & Record<string, unknown>;
          return (
            <Box component="li" key={key} {...rest} sx={{ gap: 1.25 }}>
              {option.type === 'project' ? (
                <CompanyAvatar
                  name={option.company?.name}
                  logoId={option.company?.logoId}
                  icon={option.company?.icon}
                  color={option.company?.color}
                  size={24}
                  aria-hidden
                />
              ) : (
                <PersonAvatar name={option.primary} src={option.avatarUrl} size={24} aria-hidden />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.primary', lineHeight: 1.3 }} noWrap>
                  {option.primary}
                </Typography>
                {option.secondary && (
                  <Typography sx={{ fontSize: '0.71875rem', color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                    {option.secondary}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          // MUI v9: customise the adornments by merging into params.slotProps.input
          // (keeping its `ref` + listbox wiring). The legacy InputProps/inputProps
          // props don't exist on v9 TextField.
          <TextField
            {...params}
            placeholder="Search projects, people…"
            slotProps={{
              ...params.slotProps,
              // The combobox has no visible label (prototype-faithful), so give
              // the input an accessible name for screen readers (WCAG 4.1.2).
              htmlInput: {
                ...params.slotProps.htmlInput,
                'aria-label': 'Search projects and people',
              },
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <SearchIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />
                ),
                endAdornment: loading ? (
                  <CircularProgress size={14} sx={{ color: 'text.disabled' }} />
                ) : (
                  <Box
                    component="kbd"
                    sx={{
                      fontFamily: GEIST_MONO,
                      fontSize: 11.5,
                      color: 'text.disabled',
                      bgcolor: theme.palette.surface.raised,
                      border: '1px solid',
                      borderColor: 'divider',
                      px: '5px',
                      borderRadius: 0.5,
                      lineHeight: 1.6,
                    }}
                  >
                    ⌘K
                  </Box>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: insetShadow,
                fontSize: 12.5,
                py: '2px',
                '& fieldset': { borderColor: 'divider' },
              },
            }}
          />
        )}
      />
    </Box>
  );
}
